import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Phone, Mail, MapPin, Clock, FileText, CheckCircle, ArrowRight, Calendar, Users, BookOpen, Globe, GraduationCap } from 'lucide-react';
import Layout from '@/components/Layout';
import { IMAGES } from '@/assets/images';

type TabKey = 'undergraduate' | 'graduate' | 'doctoral' | 'international';

interface AdmissionTab {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
}

interface TimelineItem {
  month: string;
  title: string;
  description: string;
}

interface Requirement {
  title: string;
  items: string[];
}

interface ContactInfo {
  icon: React.ReactNode;
  label: string;
  value: string;
}

interface TabContent {
  title: string;
  subtitle: string;
  timeline: TimelineItem[];
  requirements: Requirement[];
  contacts: ContactInfo[];
  image: string;
}

const TABS: AdmissionTab[] = [
  { key: 'undergraduate', label: '本科招生', icon: <BookOpen size={18} /> },
  { key: 'graduate', label: '研究生招生', icon: <GraduationCap size={18} /> },
  { key: 'doctoral', label: '博士招生', icon: <FileText size={18} /> },
  { key: 'international', label: '留学生招生', icon: <Globe size={18} /> },
];

const TAB_CONTENT: Record<TabKey, TabContent> = {
  undergraduate: {
    title: '本科生招生',
    subtitle: '选择南开，成就未来',
    image: IMAGES.GRADUATION_3,
    timeline: [
      { month: '6月', title: '高考报名', description: '参加全国统一高考，关注各省招生计划及投档线' },
      { month: '7月', title: '填报志愿', description: '根据高考成绩填报南开大学各专业志愿' },
      { month: '8月', title: '录取通知', description: '查询录取结果，领取录取通知书' },
      { month: '9月', title: '新生报到', description: '按时参加新生入学报到，完成注册手续' },
    ],
    requirements: [
      {
        title: '报考条件',
        items: [
          '参加全国统一高考，成绩达到相应分数线',
          '品学兼优，身体健康',
          '符合所报专业特殊要求',
          '外语水平符合专业要求',
        ],
      },
      {
        title: '特殊类型招生',
        items: [
          '强基计划：面向基础学科拔尖人才',
          '综合评价招生：结合高考与综合素质',
          '艺术类招生：美术、音乐等专业',
          '保送生招生：全国优秀中学推荐',
        ],
      },
    ],
    contacts: [
      { icon: <Phone size={16} />, label: '招生热线', value: '022-2350-6001' },
      { icon: <Mail size={16} />, label: '电子邮件', value: 'zs@nankai.edu.cn' },
      { icon: <MapPin size={16} />, label: '办公地址', value: '天津市南开区卫津路94号' },
      { icon: <Clock size={16} />, label: '办公时间', value: '周一至周五 8:30-17:00' },
    ],
  },
  graduate: {
    title: '硕士研究生招生',
    subtitle: '深耕学术，拓展视野',
    image: IMAGES.CAMPUS_8,
    timeline: [
      { month: '9月', title: '网上报名', description: '登录中国研究生招生信息网完成网上报名' },
      { month: '10月', title: '现场确认', description: '到报名点进行现场信息确认及照相' },
      { month: '12月', title: '初试考试', description: '参加全国硕士研究生入学统一考试' },
      { month: '3月', title: '复试录取', description: '通过初试分数线后参加南开大学复试' },
    ],
    requirements: [
      {
        title: '报考资格',
        items: [
          '国家承认的本科毕业生（含应届）',
          '获得国家承认的大专学历，毕业满两年',
          '已获硕士或博士学位人员',
          '同等学力考生需符合相应条件',
        ],
      },
      {
        title: '推荐免试',
        items: [
          '全国重点高校优秀应届本科毕业生',
          '在校期间学业成绩排名靠前',
          '具有较强科研创新能力',
          '参加南开大学暑期学校或直招活动',
        ],
      },
    ],
    contacts: [
      { icon: <Phone size={16} />, label: '咨询电话', value: '022-2350-8901' },
      { icon: <Mail size={16} />, label: '电子邮件', value: 'yzb@nankai.edu.cn' },
      { icon: <MapPin size={16} />, label: '办公地址', value: '天津市南开区卫津路94号研究生院' },
      { icon: <Clock size={16} />, label: '办公时间', value: '周一至周五 9:00-16:30' },
    ],
  },
  doctoral: {
    title: '博士研究生招生',
    subtitle: '追求卓越，引领前沿',
    image: IMAGES.LIBRARY_2,
    timeline: [
      { month: '11月', title: '申请报名', description: '提交申请材料，包括研究计划、成果证明等' },
      { month: '12月', title: '材料审核', description: '招生单位对申请材料进行初步审核' },
      { month: '3月', title: '综合考核', description: '参加笔试和专家面试考核' },
      { month: '5月', title: '录取公示', description: '公示拟录取名单，办理录取手续' },
    ],
    requirements: [
      {
        title: '报考资格',
        items: [
          '已获得硕士学位或即将获得硕士学位',
          '品学兼优，身心健康',
          '具有较强的科研能力和创新精神',
          '有意愿在本领域进行深入研究',
        ],
      },
      {
        title: '申请材料',
        items: [
          '学位证书及成绩单（公证件）',
          '两封专家推荐信',
          '研究计划书（3000字以上）',
          '代表性学术成果（论文、专利等）',
        ],
      },
    ],
    contacts: [
      { icon: <Phone size={16} />, label: '咨询电话', value: '022-2350-8902' },
      { icon: <Mail size={16} />, label: '电子邮件', value: 'bszs@nankai.edu.cn' },
      { icon: <MapPin size={16} />, label: '办公地址', value: '天津市南开区卫津路94号研究生院' },
      { icon: <Clock size={16} />, label: '办公时间', value: '周一至周五 9:00-16:30' },
    ],
  },
  international: {
    title: '留学生招生',
    subtitle: '感受中国，拥抱世界',
    image: IMAGES.GRADUATION_3,
    timeline: [
      { month: '3月', title: '在线申请', description: '登录南开大学国际学生招生系统提交申请' },
      { month: '4月', title: '材料审核', description: '学校对申请材料进行审核，必要时面试' },
      { month: '5月', title: '录取通知', description: '合格者将收到录取通知书及签证材料' },
      { month: '9月', title: '入学报到', description: '携带相关材料到学校报到注册' },
    ],
    requirements: [
      {
        title: '申请条件',
        items: [
          '非中国国籍，身体健康',
          '本科申请：高中毕业，成绩优良',
          '硕博申请：具有相应学历背景',
          '汉语水平达到HSK相应等级',
        ],
      },
      {
        title: '奖学金类型',
        items: [
          '中国政府奖学金（CSC）',
          '天津市外国留学生奖学金',
          '南开大学校长奖学金',
          '丝绸之路留学生奖学金',
        ],
      },
    ],
    contacts: [
      { icon: <Phone size={16} />, label: '国际合作处', value: '022-2350-2816' },
      { icon: <Mail size={16} />, label: '电子邮件', value: 'nkiso@nankai.edu.cn' },
      { icon: <MapPin size={16} />, label: '办公地址', value: '天津市南开区卫津路94号主楼E区' },
      { icon: <Clock size={16} />, label: '办公时间', value: '周一至周五 9:00-17:00' },
    ],
  },
};

const APPLICATION_STEPS = [
  { step: '01', title: '了解招生信息', desc: '查阅招生简章、专业介绍及录取要求', icon: <FileText size={20} /> },
  { step: '02', title: '准备申请材料', desc: '学历证明、成绩单、推荐信等必要材料', icon: <CheckCircle size={20} /> },
  { step: '03', title: '网上报名提交', desc: '登录招生系统，填写报名表并上传材料', icon: <Users size={20} /> },
  { step: '04', title: '参加考核面试', desc: '参加笔试或面试，展示综合能力', icon: <Calendar size={20} /> },
  { step: '05', title: '等待录取通知', desc: '查询录取结果，领取录取通知书', icon: <GraduationCap size={20} /> },
  { step: '06', title: '完成入学报到', desc: '按时报到，办理学籍注册手续', icon: <ArrowRight size={20} /> },
];

const FAQ_ITEMS = [
  {
    q: '南开大学有哪些校区？招生范围是否一致？',
    a: '南开大学设有三个校区：八里台校区（本部）、津南校区和泰达校区。不同年级和专业在不同校区就读，招生计划在全国各省均有分配，具体以当年招生计划为准。',
  },
  {
    q: '报考南开大学需要具备哪些外语能力？',
    a: '本科生入学后须参加大学英语学习，部分专业对外语有特殊要求。研究生阶段需通过全国英语六级或提供相应英语水平证明。留学生需提供HSK成绩或其他语言能力证明。',
  },
  {
    q: '南开大学提供哪些奖学金？',
    a: '学校设有多层次奖学金体系，包括国家奖学金、国家励志奖学金、南开大学优秀学生奖学金等。研究生还可申请国家研究生奖学金和各类科研助学金。留学生可申请多种政府和校级奖学金。',
  },
  {
    q: '推免生（保送生）的申请流程是怎样的？',
    a: '推免生须在全国推免服务系统开放后进行申请，南开大学接受来自全国知名高校的优秀应届毕业生申请。申请时需提交成绩单、科研成果等材料，通过综合考核后方可获得接收资格。',
  },
  {
    q: '南开大学是否支持出国交流项目？',
    a: '南开大学与全球280余所知名高校签署了合作协议，提供丰富的国际交流项目，包括交换生项目、联合培养项目、暑期学校等。学生可通过国际合作处了解最新交流机会。',
  },
  {
    q: '在职人员可以报考南开大学研究生吗？',
    a: '可以。南开大学接受在职人员参加全国统考报考学术型和专业型研究生。在职人员还可报考南开大学提供的在职研究生教育项目，具体要求请咨询研究生院招生办公室。',
  },
];

function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {FAQ_ITEMS.map((item, idx) => (
        <div
          key={idx}
          className="border border-border rounded-lg overflow-hidden"
        >
          <button
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
            className="w-full flex items-center justify-between px-6 py-4 text-left bg-card hover:bg-accent/50 transition-colors"
          >
            <span className="font-medium text-card-foreground pr-4">{item.q}</span>
            <ChevronDown
              size={18}
              className={`text-muted-foreground flex-shrink-0 transition-transform duration-200 ${
                openIndex === idx ? 'rotate-180' : ''
              }`}
            />
          </button>
          <AnimatePresence>
            {openIndex === idx && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="px-6 py-4 bg-muted/30 text-muted-foreground leading-relaxed border-t border-border">
                  {item.a}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}

export default function Admissions() {
  const [activeTab, setActiveTab] = useState<TabKey>('undergraduate');
  const content = TAB_CONTENT[activeTab];

  return (
    <Layout>
      <div className="relative h-[480px] overflow-hidden">
        <img
          src={IMAGES.GRADUATION_3}
          alt="南开大学招生"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/60 via-primary/40 to-background/80" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="text-center px-4"
          >
            <p className="text-primary-foreground/80 text-sm font-medium tracking-widest mb-3 uppercase">Nankai University</p>
            <h1
              className="text-4xl md:text-6xl font-bold text-white mb-4"
              style={{ fontFamily: 'Noto Serif SC, serif' }}
            >
              招生就业
            </h1>
            <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
              允公允能，日新月异 — 迎接新一代南开人的加入
            </p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                    activeTab === tab.key
                      ? 'bg-white text-primary shadow-lg'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <div className="py-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full mb-4">
                    {TABS.find((t) => t.key === activeTab)?.label}
                  </span>
                  <h2
                    className="text-3xl md:text-4xl font-bold text-foreground mb-4"
                    style={{ fontFamily: 'Noto Serif SC, serif' }}
                  >
                    {content.title}
                  </h2>
                  <p className="text-muted-foreground text-lg mb-8">{content.subtitle}</p>

                  <div className="space-y-4">
                    {content.timeline.map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex gap-4 items-start"
                      >
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-bold text-sm" style={{ fontFamily: 'Source Code Pro, monospace' }}>
                            {item.month}
                          </span>
                        </div>
                        <div className="flex-1 pt-1">
                          <h4 className="font-semibold text-foreground">{item.title}</h4>
                          <p className="text-muted-foreground text-sm mt-1">{item.description}</p>
                        </div>
                        {idx < content.timeline.length - 1 && (
                          <div className="absolute left-6 mt-12 w-0.5 h-8 bg-border" style={{ position: 'static' }} />
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <div className="rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={content.image}
                      alt={content.title}
                      className="w-full h-80 object-cover"
                    />
                  </div>
                  <div
                    className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground rounded-xl p-5 shadow-lg"
                    style={{ maxWidth: '200px' }}
                  >
                    <p className="text-3xl font-bold" style={{ fontFamily: 'Source Code Pro, monospace' }}>985</p>
                    <p className="text-primary-foreground/80 text-sm mt-1">双一流A类高校</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="py-12 border-t border-border">
              <h3
                className="text-2xl font-bold text-foreground mb-8 text-center"
                style={{ fontFamily: 'Noto Serif SC, serif' }}
              >
                报考要求
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {content.requirements.map((req, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.15 }}
                    className="bg-card border border-border rounded-xl p-6"
                  >
                    <h4 className="font-semibold text-card-foreground mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-primary inline-block" />
                      {req.title}
                    </h4>
                    <ul className="space-y-2">
                      {req.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle size={14} className="text-primary mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="py-12 border-t border-border">
              <h3
                className="text-2xl font-bold text-foreground mb-8 text-center"
                style={{ fontFamily: 'Noto Serif SC, serif' }}
              >
                联系方式
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {content.contacts.map((contact, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-card border border-border rounded-xl p-5 hover:border-primary/50 hover:shadow-md transition-all"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-3">
                      {contact.icon}
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">{contact.label}</p>
                    <p className="font-medium text-card-foreground text-sm">{contact.value}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="py-16 border-t border-border">
          <div className="text-center mb-12">
            <h2
              className="text-3xl font-bold text-foreground mb-3"
              style={{ fontFamily: 'Noto Serif SC, serif' }}
            >
              申请流程
            </h2>
            <p className="text-muted-foreground">六步完成您的南开大学申请之旅</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {APPLICATION_STEPS.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative text-center"
              >
                <div className="bg-card border border-border rounded-xl p-4 hover:border-primary/50 hover:shadow-md transition-all group">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mx-auto mb-3 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    {step.icon}
                  </div>
                  <div
                    className="text-xs font-bold text-chart-1 mb-1"
                    style={{ fontFamily: 'Source Code Pro, monospace' }}
                  >
                    {step.step}
                  </div>
                  <h4 className="font-semibold text-card-foreground text-sm mb-1">{step.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
                </div>
                {idx < APPLICATION_STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-2 z-10 text-border">
                    <ArrowRight size={16} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        <div className="py-16 border-t border-border">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-1">
              <h2
                className="text-3xl font-bold text-foreground mb-3"
                style={{ fontFamily: 'Noto Serif SC, serif' }}
              >
                常见问题
              </h2>
              <p className="text-muted-foreground mb-6">解答您关于招生的常见疑问</p>
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src={IMAGES.CAMPUS_8}
                  alt="南开大学校园"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent flex items-end p-4">
                  <p className="text-white text-sm font-medium">如有更多疑问，欢迎联系招生办公室</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <FAQAccordion />
            </div>
          </div>
        </div>

        <div className="py-16 border-t border-border mb-8">
          <div
            className="rounded-2xl p-8 md:p-12 text-center"
            style={{
              background: 'linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 70%, black) 100%)',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-xl overflow-hidden mb-8 max-w-2xl mx-auto">
                <img
                  src={IMAGES.LIBRARY_2}
                  alt="南开大学图书馆"
                  className="w-full h-48 object-cover opacity-30"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <p
                    className="text-white text-3xl md:text-4xl font-bold"
                    style={{ fontFamily: 'Noto Serif SC, serif' }}
                  >
                    允公允能 · 日新月异
                  </p>
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                加入南开，共创美好未来
              </h3>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                百余年薪火相传，南开大学期待与您共同书写新的精彩篇章
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href="#"
                  className="px-8 py-3 bg-white text-primary font-semibold rounded-full hover:bg-white/90 transition-colors shadow-lg"
                >
                  立即申请
                </a>
                <a
                  href="#"
                  className="px-8 py-3 bg-white/20 text-white font-semibold rounded-full hover:bg-white/30 transition-colors border border-white/30"
                >
                  下载招生简章
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
